import {
  Menu,
  Stack,
  Typography,
  TextField,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import { ChangeEvent, KeyboardEventHandler, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import { CheckMarkIcon, CreateNewIcon, TagsIcon } from "../../assets/Icons";
import { useDispatch, useSelector } from "react-redux";
import { TagsSagaActions } from "../../store/tags/sagas";
import { TagsListsSelector } from "../../store/metadata/selector";
import {
  tagsLoadingSelector,
  tagStateSelector,
} from "../../store/tags/selector";
import { TagsReducerAction } from "../../store/tags/slice";
import { DiscoveryItem } from "../../store/discovery/types";

type TagItem = {
  _id: string;
  name: string;
};

const AddTagPopover = ({
  anchorEl,
  handlePopoverClose,
  item,
}: {
  anchorEl: null | Element;
  handlePopoverClose: () => void;
  item: DiscoveryItem;
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const tagsList: TagItem[] = useSelector(TagsListsSelector);
  const tagsLoading = useSelector(tagsLoadingSelector);
  const { newTagValue } = useSelector(tagStateSelector);

  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);

  const selectedTagsId = useMemo(
    () => item?.Tags?.map((tag) => tag._id) || [],
    [item?.Tags]
  );

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(TagsReducerAction.setNewTagValue(e.target.value));
  };

  const handleAddTag: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === "Enter" && newTagValue.trim()) {
      dispatch(TagsSagaActions.createTag(item._id));
    }
  };

  const addOrRemoveTag = (tagId: string) => {
    setSelectedTagId(tagId);
    dispatch(
      TagsSagaActions.addOrRemoveTagsFromAd({
        tagId,
        adId: item._id,
      })
    );
  };

  const filteredTags = useMemo(() => {
    const filtered = tagsList.filter((tag) =>
      tag.name.toLowerCase().includes(newTagValue.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const aSelected = selectedTagsId.includes(a._id);
      const bSelected = selectedTagsId.includes(b._id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0; // preserve original order otherwise
    });
  }, [newTagValue, tagsList, selectedTagsId]);

  const tagExists = filteredTags.length > 0;

  const getIcon = (tagId: string) => {
    if (tagsLoading && selectedTagId === tagId) {
      return <CircularProgress size={15} />;
    }
    if (selectedTagsId.includes(tagId)) {
      return <CheckMarkIcon />;
    }
    return null;
  };

  return (
    <Menu
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handlePopoverClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{
        ".MuiMenu-paper": {
          borderRadius: "12px",
          padding: "1rem",
          width: "300px",
        },
        ".MuiList-root": {
          padding: 0,
        },
      }}
    >
      <Box display="flex" flexDirection="column" gap="1rem">
        <TextField
          value={newTagValue}
          onChange={handleSearchChange}
          onKeyDown={handleAddTag}
          placeholder="Search or create tag..."
          size="small"
          fullWidth
        />

        <Box
          display="flex"
          flexDirection="column"
          gap="0.5rem"
          maxHeight="200px"
          overflow="auto"
        >
          {filteredTags.map((tag) => (
            <Stack
              key={tag._id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                cursor: "pointer",
                padding: "0.5rem",
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: theme.colors.background4,
                },
              }}
              onClick={() => addOrRemoveTag(tag._id)}
            >
              <Stack direction="row" alignItems="center" gap="0.5rem">
                <TagsIcon />
                <Typography
                  fontSize="0.9rem"
                  color={theme.colors.black2}
                  noWrap
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "180px"
                  }}
                >
                  {tag.name}
                </Typography>
              </Stack>
              {getIcon(tag._id)}
            </Stack>
          ))}

          {!tagExists && newTagValue.trim() && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => dispatch(TagsSagaActions.createTag(item._id))}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                padding: "0.5rem",
                borderColor: theme.colors.black2,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                color: `${theme.colors.black2} !important`,
              }}
            >
              <CreateNewIcon fill={theme.colors.black2} />
              Create "{newTagValue}"
            </Button>
          )}
        </Box>
      </Box>
    </Menu>
  );
};

export default AddTagPopover;
